import { useState, useEffect, useCallback } from 'react';
import PanelHeader from './PanelHeader';
import BasicInfoTab from './tabs/BasicInfoTab';
import DeviceTab from './tabs/DeviceTab';
import ContractTab from './tabs/ContractTab';
import SettingLogTab from './tabs/SettingLogTab';
import CSLogTab from './tabs/CSLogTab';
import {
    useTownDetail,
    useTownUpdate,
    useTownDevices,
    useSaveDevices,
    useContractUpdate,
    useContractUpload,
    useSettingLogs,
    useAddSettingLog,
    useCSLogs,
    useAddCSLog,
    useTownStatusChange,
    useTownDelete,
} from '../../hooks/useTownDetail';
import type { TownDevice } from '@shared/types/town';

type TabId = 'info' | 'device' | 'contract' | 'setting-log' | 'cs-log';

const TABS: { id: TabId; label: string }[] = [
    { id: 'info', label: '기본 정보' },
    { id: 'device', label: '디바이스·장비' },
    { id: 'contract', label: '계약·일정' },
    { id: 'setting-log', label: '세팅 메모' },
    { id: 'cs-log', label: '단지 메모/CS' },
];

interface TownDetailPanelProps {
    townId: number;
    onClose: () => void;
}

export default function TownDetailPanel({ townId, onClose }: TownDetailPanelProps) {
    const [activeTab, setActiveTab] = useState<TabId>('info');
    const [saveCounter, setSaveCounter] = useState(0);

    // Data queries
    const { data: town, isLoading: townLoading } = useTownDetail(townId);
    const { data: devices = [] } = useTownDevices(townId);
    const { data: settingLogs = [], isLoading: settingLogsLoading } = useSettingLogs(townId);
    const { data: csLogs = [], isLoading: csLogsLoading } = useCSLogs(townId);

    // Mutations
    const townUpdate = useTownUpdate(townId);
    const saveDevicesMut = useSaveDevices(townId);
    const contractUpdate = useContractUpdate(townId);
    const contractUploadMut = useContractUpload(townId);
    const addSettingLogMut = useAddSettingLog(townId);
    const addCSLogMut = useAddCSLog(townId);
    const statusChangeMut = useTownStatusChange(townId);
    const deleteMut = useTownDelete(townId);

    // Status change handlers — close panel after status change
    const handleExclude = useCallback(() => {
        statusChangeMut.mutate('excluded', {
            onSuccess: () => onClose(),
        });
    }, [statusChangeMut, onClose]);

    const handleRestore = useCallback(() => {
        statusChangeMut.mutate('active', {
            onSuccess: () => onClose(),
        });
    }, [statusChangeMut, onClose]);

    // Delete handler — confirm then delete
    const handleDelete = useCallback(() => {
        if (!town) return;
        if (!window.confirm(`"${town.name}" 단지를 완전히 삭제하시겠습니까?\n\n삭제하면 모든 데이터(디바이스, 로그 등)가 함께 삭제되며 복구할 수 없습니다.`)) return;
        deleteMut.mutate(undefined, {
            onSuccess: () => onClose(),
        });
    }, [deleteMut, onClose, town]);

    // ESC key to close
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                if (activeTab === 'info' || activeTab === 'device' || activeTab === 'contract') {
                    setSaveCounter((c) => c + 1);
                }
                setTimeout(() => onClose(), 100);
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [onClose, activeTab]);

    // Prevent body scroll
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = '';
        };
    }, []);

    // Save handlers — include _updatedAt for optimistic locking
    const handleBasicInfoSave = useCallback(
        (data: Record<string, unknown>) => {
            townUpdate.mutate({ ...data, _updatedAt: town?.updated_at } as any, {
                onError: (err: any) => {
                    if (err?.response?.status === 409) {
                        alert(err.response.data.error || '다른 사용자가 이미 수정했습니다. 새로고침 후 다시 시도해주세요.');
                    }
                },
            });
        },
        [townUpdate, town?.updated_at],
    );

    const handleDeviceSave = useCallback(
        (data: { townFields: Record<string, unknown>; devices: TownDevice[] }) => {
            townUpdate.mutate({ ...data.townFields, _updatedAt: town?.updated_at } as any, {
                onError: (err: any) => {
                    if (err?.response?.status === 409) {
                        alert(err.response.data.error || '다른 사용자가 이미 수정했습니다. 새로고침 후 다시 시도해주세요.');
                    }
                },
            });
            saveDevicesMut.mutate(data.devices);
        },
        [townUpdate, saveDevicesMut, town?.updated_at],
    );

    const handleContractSave = useCallback(
        (data: Record<string, unknown>) => {
            contractUpdate.mutate({ ...data, _updatedAt: town?.updated_at } as any, {
                onError: (err: any) => {
                    if (err?.response?.status === 409) {
                        alert('다른 사용자가 이미 수정했습니다. 새로고침 후 다시 시도해주세요.');
                    }
                },
            });
        },
        [contractUpdate, town?.updated_at],
    );

    const handleContractUpload = useCallback(
        (file: File) => {
            contractUploadMut.mutate(file);
        },
        [contractUploadMut],
    );

    // Footer save increments counter — tabs watch this to trigger save
    const handleFooterSave = () => {
        setSaveCounter((c) => c + 1);
    };

    const isSaving =
        townUpdate.isPending || saveDevicesMut.isPending || contractUpdate.isPending;

    if (townLoading || !town) {
        return (
            <div className="fixed inset-0 z-[100]">
                <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
                <div className="absolute right-0 top-0 h-full w-[780px] bg-white shadow-2xl flex items-center justify-center">
                    <div className="text-sm text-gray-400">불러오는 중...</div>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-[100]">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

            {/* Panel */}
            <div
                className="absolute right-0 top-0 h-full w-[780px] bg-white shadow-2xl flex flex-col"
                style={{ animation: 'slideIn .25s ease' }}
            >
                {/* Header */}
                <PanelHeader
                    town={town}
                    onClose={onClose}
                    onExclude={handleExclude}
                    onRestore={handleRestore}
                    isStatusChanging={statusChangeMut.isPending}
                />

                {/* Tab bar */}
                <div className="px-7 pt-3 border-b border-[#E6E9ED]">
                    <div className="flex gap-0">
                        {TABS.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => {
                                    if (tab.id !== activeTab && (activeTab === 'info' || activeTab === 'device' || activeTab === 'contract')) {
                                        setSaveCounter((c) => c + 1);
                                    }
                                    setActiveTab(tab.id);
                                }}
                                className={`px-4 py-2.5 text-xs border-b-2 transition ${activeTab === tab.id
                                    ? 'font-semibold text-[#1ABB9C] border-[#1ABB9C]'
                                    : 'font-medium text-[#73879C] border-transparent hover:text-[#2A3F54]'
                                    }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-7 py-6">
                    {activeTab === 'info' && (
                        <BasicInfoTab
                            town={town}
                            onSave={handleBasicInfoSave}
                            isSaving={townUpdate.isPending}
                            saveSignal={saveCounter}
                        />
                    )}
                    {activeTab === 'device' && (
                        <DeviceTab
                            town={town}
                            devices={devices}
                            onSave={handleDeviceSave}
                            isSaving={saveDevicesMut.isPending || townUpdate.isPending}
                            saveSignal={saveCounter}
                        />
                    )}
                    {activeTab === 'contract' && (
                        <ContractTab
                            town={town}
                            onSave={handleContractSave}
                            onUpload={handleContractUpload}
                            isSaving={contractUpdate.isPending}
                            isUploading={contractUploadMut.isPending}
                            saveSignal={saveCounter}
                        />
                    )}
                    {activeTab === 'setting-log' && (
                        <SettingLogTab
                            logs={settingLogs}
                            isLoading={settingLogsLoading}
                            onAdd={(payload) => addSettingLogMut.mutate(payload)}
                            isAdding={addSettingLogMut.isPending}
                        />
                    )}
                    {activeTab === 'cs-log' && (
                        <CSLogTab
                            logs={csLogs}
                            isLoading={csLogsLoading}
                            onAdd={(payload) => addCSLogMut.mutate(payload)}
                            isAdding={addCSLogMut.isPending}
                            townId={townId}
                        />
                    )}
                </div>

                {/* Footer */}
                <div className="px-7 py-4 border-t border-[#E6E9ED] flex items-center justify-between bg-white">
                    <button
                        onClick={handleDelete}
                        disabled={deleteMut.isPending}
                        className="px-4 py-2 text-xs font-medium text-red-500 border border-red-200 rounded hover:bg-red-50 transition disabled:opacity-50"
                    >
                        {deleteMut.isPending ? '삭제 중...' : '삭제'}
                    </button>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => {
                                if (activeTab === 'info' || activeTab === 'device' || activeTab === 'contract') {
                                    setSaveCounter((c) => c + 1);
                                }
                                setTimeout(() => onClose(), 100);
                            }}
                            className="px-4 py-2 text-xs font-medium border border-[#E6E9ED] rounded hover:bg-[#F5F7FA] transition"
                        >
                            닫기
                        </button>
                        {(activeTab === 'info' || activeTab === 'device' || activeTab === 'contract') && (
                            <button
                                onClick={handleFooterSave}
                                disabled={isSaving}
                                className="px-5 py-2 text-xs font-medium bg-[#1ABB9C] text-white rounded hover:bg-[#169F85] transition shadow-sm disabled:opacity-50"
                            >
                                {isSaving ? '저장 중...' : '저장'}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
